import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import { LimitedTextField } from '@/components/molecules/LimitedTextField';
import { Group } from '@/DTOs/groups';
import { UseFormWithErrorsReturn } from '@/hooks/useFormWithErrors';
import { useTranslator } from '@/hooks/useTranslator';
import { APPLICATION_FUNCTIONALITY, GroupFormData } from '@/schemas/group';

interface Props {
  readonly form: UseFormWithErrorsReturn<GroupFormData>;
  readonly initialValues?: Partial<Group>;
  readonly loading?: boolean;
}

export const GroupFormFields: React.FC<Props> = (props: Props): React.ReactElement => {
  const { form, initialValues = emptyGroup, loading = false } = props;
  const { register } = form;

  const tr = useTranslator();

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <TextField
        defaultValue={initialValues.groupName}
        {...register({ name: 'groupName', label: tr('ROLE_NAME') })}
        disabled={loading}
        fullWidth={true}
      />

      <LimitedTextField
        variant="outlined"
        maxLength={200}
        multiline={true}
        minRows={3}
        maxRows={4}
        fullWidth={true}
        defaultValue={initialValues.description}
        {...register({ name: 'description', label: tr('DESCRIPTION_OF_ROLE_OPTIONAL') })}
        disabled={loading}
      />

      <Box mt={1}>
        <Typography variant="h7">{tr('FUNCTIONALITIES')}</Typography>
      </Box>

      <Box display="flex" flexDirection="column" gap={0}>
        {APPLICATION_FUNCTIONALITY.map((functionality: string): React.ReactElement => {
          const {
            helperText: _helperText,
            error: _error,
            ...properties
          } = register({ name: 'roles', label: tr(functionality) });
          const { roles } = initialValues;

          return (
            <Box
              key={functionality}
              mx={1 /* needed because we are forcing the UI library due to design choices */}>
              <FormControlLabel
                sx={styles.functionality}
                control={
                  <Checkbox
                    value={functionality}
                    defaultChecked={roles?.includes(functionality)}
                    disabled={loading}
                  />
                }
                {...properties}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  functionality: {
    display: 'flex',
    alignItems: 'center',
    color: 'text.primary',
  },
};

const emptyGroup: Omit<Group, 'groupId'> = {
  groupName: '',
  description: '',
  roles: [],
};
